import type { ListParams } from '@nucleus-commerce/admin-kit/index-table';
import {
  getAdminV1StoresStoreIdOrders,
  getAdminV1StoresStoreIdOrdersId,
  getAdminV1StoresStoreIdOrdersIdDocumentsKind,
  getAdminV1StoresStoreIdOrdersIdNotifications,
  patchAdminV1StoresStoreIdOrdersId,
  patchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentId,
  patchAdminV1StoresStoreIdOrdersIdLinesLineId,
  postAdminV1StoresStoreIdOrdersBulk,
  postAdminV1StoresStoreIdOrdersIdApprovalRecord,
  postAdminV1StoresStoreIdOrdersIdArchive,
  postAdminV1StoresStoreIdOrdersIdCancel,
  postAdminV1StoresStoreIdOrdersIdDesign,
  postAdminV1StoresStoreIdOrdersIdEmailsResend,
  postAdminV1StoresStoreIdOrdersIdFulfillments,
  postAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdCancel,
  postAdminV1StoresStoreIdOrdersIdHold,
  postAdminV1StoresStoreIdOrdersIdMarkPaid,
  postAdminV1StoresStoreIdOrdersIdNotes,
  postAdminV1StoresStoreIdOrdersIdRefunds,
  postAdminV1StoresStoreIdOrdersIdRelease,
  postAdminV1StoresStoreIdExports,
  type BulkOrdersResult,
  type ExportFiltersInput,
  type ExportJob,
  type GetAdminV1StoresStoreIdOrders200,
  type GetAdminV1StoresStoreIdOrdersIdNotifications200,
  type Order,
  type OrderDocument,
  type OrderDocumentKind,
  type OrderNotification,
  type PatchAdminV1StoresStoreIdOrdersIdBody,
  type PatchAdminV1StoresStoreIdOrdersIdLinesLineIdBody,
  type PostAdminV1StoresStoreIdOrdersBulkBody,
  type PostAdminV1StoresStoreIdOrdersIdApprovalRecordBody,
  type PostAdminV1StoresStoreIdOrdersIdEmailsResendBody,
  type PatchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdBody,
  type PostAdminV1StoresStoreIdOrdersIdCancelBody,
  type PostAdminV1StoresStoreIdOrdersIdDesignBody,
  type PostAdminV1StoresStoreIdOrdersIdFulfillmentsBody,
  type PostAdminV1StoresStoreIdOrdersIdHoldBody,
  type PostAdminV1StoresStoreIdOrdersIdMarkPaidBody,
  type PostAdminV1StoresStoreIdOrdersIdNotesBody,
  type PostAdminV1StoresStoreIdOrdersIdRefundsBody,
} from '@nucleus-commerce/api-client/admin';

/**
 * List params as `listParams` builds them from the route's search: `q`, `sort`, the flat filter
 * params (`status=open,completed`, `total_gte=5000`), `cursor`, `limit`.
 */
export type OrderListParams = ListParams;

/** Orders of one store. Every action returns the full order, timeline included. */
export const ordersApi = {
  list: (storeId: string, params: OrderListParams): Promise<GetAdminV1StoresStoreIdOrders200> =>
    getAdminV1StoresStoreIdOrders({ storeId }, params),
  get: (storeId: string, id: string): Promise<Order> =>
    getAdminV1StoresStoreIdOrdersId({ storeId, id }),
  notifications: (
    storeId: string,
    id: string,
  ): Promise<GetAdminV1StoresStoreIdOrdersIdNotifications200> =>
    getAdminV1StoresStoreIdOrdersIdNotifications({ storeId, id }),
  update: (
    storeId: string,
    id: string,
    body: PatchAdminV1StoresStoreIdOrdersIdBody,
  ): Promise<Order> => patchAdminV1StoresStoreIdOrdersId({ storeId, id }, body),
  markPaid: (
    storeId: string,
    id: string,
    body: PostAdminV1StoresStoreIdOrdersIdMarkPaidBody,
  ): Promise<Order> => postAdminV1StoresStoreIdOrdersIdMarkPaid({ storeId, id }, body),
  cancel: (
    storeId: string,
    id: string,
    body: PostAdminV1StoresStoreIdOrdersIdCancelBody,
  ): Promise<Order> => postAdminV1StoresStoreIdOrdersIdCancel({ storeId, id }, body),
  addNote: (
    storeId: string,
    id: string,
    body: PostAdminV1StoresStoreIdOrdersIdNotesBody,
  ): Promise<Order> => postAdminV1StoresStoreIdOrdersIdNotes({ storeId, id }, body),
  hold: (
    storeId: string,
    id: string,
    body: PostAdminV1StoresStoreIdOrdersIdHoldBody,
  ): Promise<Order> => postAdminV1StoresStoreIdOrdersIdHold({ storeId, id }, body),
  /** `force` lifts a design-approval hold too (needs `orders.edit`); otherwise 409 ORDER_APPROVAL_PENDING. */
  release: (storeId: string, id: string, force = false): Promise<Order> =>
    postAdminV1StoresStoreIdOrdersIdRelease({ storeId, id }, force ? { force: true } : {}),
  fulfill: (
    storeId: string,
    id: string,
    body: PostAdminV1StoresStoreIdOrdersIdFulfillmentsBody,
  ): Promise<Order> => postAdminV1StoresStoreIdOrdersIdFulfillments({ storeId, id }, body),
  updateFulfillment: (
    storeId: string,
    id: string,
    fulfillmentId: string,
    body: PatchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdBody,
  ): Promise<Order> =>
    patchAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentId(
      { storeId, id, fulfillmentId },
      body,
    ),
  refund: (
    storeId: string,
    id: string,
    body: PostAdminV1StoresStoreIdOrdersIdRefundsBody,
  ): Promise<Order> => postAdminV1StoresStoreIdOrdersIdRefunds({ storeId, id }, body),
  sendDesign: (
    storeId: string,
    id: string,
    body: PostAdminV1StoresStoreIdOrdersIdDesignBody,
  ): Promise<Order> => postAdminV1StoresStoreIdOrdersIdDesign({ storeId, id }, body),
  recordApproval: (
    storeId: string,
    id: string,
    body: PostAdminV1StoresStoreIdOrdersIdApprovalRecordBody,
  ): Promise<Order> => postAdminV1StoresStoreIdOrdersIdApprovalRecord({ storeId, id }, body),
  archive: (storeId: string, id: string): Promise<Order> =>
    postAdminV1StoresStoreIdOrdersIdArchive({ storeId, id }),
  /** The full set of answers for an unshipped line; the price delta must stay the same. */
  updateLine: (
    storeId: string,
    id: string,
    lineId: string,
    body: PatchAdminV1StoresStoreIdOrdersIdLinesLineIdBody,
  ): Promise<Order> => patchAdminV1StoresStoreIdOrdersIdLinesLineId({ storeId, id, lineId }, body),
  /** Pending fulfilments only; their lines become open again. */
  cancelFulfillment: (storeId: string, id: string, fulfillmentId: string): Promise<Order> =>
    postAdminV1StoresStoreIdOrdersIdFulfillmentsFulfillmentIdCancel({ storeId, id, fulfillmentId }),
  /** 202 with the queued send-log row; 422 LIMIT_EXCEEDED after 5 resends per template per hour. */
  resendEmail: (
    storeId: string,
    id: string,
    body: PostAdminV1StoresStoreIdOrdersIdEmailsResendBody,
  ): Promise<OrderNotification> =>
    postAdminV1StoresStoreIdOrdersIdEmailsResend({ storeId, id }, body),
  document: (storeId: string, id: string, kind: OrderDocumentKind): Promise<OrderDocument> =>
    getAdminV1StoresStoreIdOrdersIdDocumentsKind({ storeId, id, kind }),
  /** Queues an orders CSV export with the list's filters or picked ids; the job runs on the worker. */
  exportOrders: (storeId: string, filters: ExportFiltersInput): Promise<ExportJob> =>
    postAdminV1StoresStoreIdExports({ storeId }, { kind: 'orders_csv', filters }),
  /** One action on up to 100 orders; each row succeeds or fails on its own. */
  bulk: (
    storeId: string,
    body: PostAdminV1StoresStoreIdOrdersBulkBody,
  ): Promise<BulkOrdersResult> => postAdminV1StoresStoreIdOrdersBulk({ storeId }, body),
};
